import { AppConfig } from '../types';
import { getTenantConfig } from '../tenants';

const tenant = getTenantConfig();
const isRedexTenant = tenant.id === 'redex';

const faviconFile = process.env.REACT_APP_FAVICON_FILE || 'Logos/favicon.png';
const faviconPath = faviconFile.startsWith('/') ? faviconFile : `/${faviconFile}`;

// Environment-based configuration
const config: AppConfig = {
  apiBaseUrl: tenant.defaults.apiBaseUrl,
  wsUrl: tenant.defaults.wsUrl,
  web3ModalProjectId: process.env.REACT_APP_WEB3MODAL_PROJECT_ID || '',
  googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '',
  
  // Supported blockchain networks
  supportedChains: [
    1, // Ethereum Mainnet
    11155111, // Sepolia Testnet
    1440002, // XRP EVM Sidechain Devnet
  ],
  defaultChain: process.env.NODE_ENV === 'production' ? 1 : 11155111,
  
  // Smart contract addresses (will be updated after deployment)
  contractAddresses: {
    propertyToken: process.env.REACT_APP_PROPERTY_TOKEN_ADDRESS || '',
    stakingVault: process.env.REACT_APP_STAKING_VAULT_ADDRESS || '',
  },
  
  // Feature flags
  features: {
    kyc: true,
    staking: true,
    chat: process.env.REACT_APP_ENABLE_CHAT === 'true',
    notifications: true,
    // Onboarding banner — flip to true once backend GET /auth/onboarding-status is deployed
    onboardingBanner: process.env.REACT_APP_ENABLE_ONBOARDING_BANNER === 'true',
  },
};

// Validation
if (!config.web3ModalProjectId && process.env.NODE_ENV === 'production') {
  console.warn('WEB3MODAL_PROJECT_ID is not set. Wallet connection may not work properly.');
}

export default config;

// API endpoints configuration
export const API_ENDPOINTS = {
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    forgotPassword: '/auth/forgot-password',
    resetPassword: '/auth/reset-password',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    walletConnect: '/auth/wallet-connect',
    kycInitiate: '/plaid/idv/session',  // Updated to use Plaid IDV
  },
  properties: {
    list: '/properties',
    create: '/properties',
    get: (id: string) => `/properties/${id}`,
    update: (id: string) => `/properties/${id}`,
    delete: (id: string) => `/properties/${id}`,
    uploadImage: (id: string) => `/properties/${id}/upload`,
  },
  transactions: {
    list: '/transactions',
    buy: '/transactions/buy',
    sell: '/transactions/sell',
    history: '/transactions/history',
  },
  staking: {
    portfolio: '/staking/portfolio',
    stake: '/staking/stake',
    unstake: (id: string) => `/staking/${id}/unstake`,
    claim: '/staking/claim-dividends',
  },
  
  // Portfolio endpoints
  portfolio: {
    summary: '/portfolio/summary',
    positions: '/portfolio/positions',
    performance: '/portfolio/performance',
    dividends: '/portfolio/dividends',
  },

  // Wallet endpoints
  wallet: {
    summary: '/wallet/summary',
    transactions: '/wallet/transactions',
    balance: '/wallet/balance',
    addFunds: '/wallet/add-funds',
    withdraw: '/wallet/withdraw',
  },
  // Bank endpoints for ACH funding
  bank: {
    linkSession: '/bank/link-session',
    accounts: '/bank/accounts',
    deposit: '/bank/deposit',
    balance: '/bank/balance',
    confirm: '/bank/confirm',
  },
  users: {
    profile: '/users/profile',
    updateProfile: '/users/profile',
  },
  admin: {
    metrics: '/admin/metrics',
    users: '/admin/users',
    properties: '/admin/properties',
    uploadProperties: '/admin/upload-properties-csv',
    auditLogs: '/admin/audit-logs',
  },
} as const;

// Blockchain network configurations
export const NETWORK_CONFIGS = {
  1: {
    name: 'Ethereum Mainnet',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: ['https://mainnet.infura.io/v3/'],
    blockExplorerUrls: ['https://etherscan.io'],
  },
  11155111: {
    name: 'Sepolia Testnet',
    nativeCurrency: { name: 'Sepolia Ether', symbol: 'SEP', decimals: 18 },
    rpcUrls: ['https://sepolia.infura.io/v3/'],
    blockExplorerUrls: ['https://sepolia.etherscan.io'],
  },
  1440002: {
    name: 'XRP EVM Sidechain Devnet',
    nativeCurrency: { name: 'XRP', symbol: 'XRP', decimals: 6 },
    rpcUrls: ['https://rpc-evm-sidechain.xrpl.org'],
    blockExplorerUrls: ['https://evm-sidechain.xrpl.org'],
  },
} as const;

// Brand assets (logos and images)
export const BRAND_ASSETS = {
  logo: {
    // Primary logo - all black version for light backgrounds
    primary: tenant.branding.logoPrimaryPath,
    // Alternative logo variants
    allBlack: isRedexTenant ? tenant.branding.logoPrimaryPath : '/Logos/logo-all-black.png',
    allWhite: isRedexTenant ? tenant.branding.logoPrimaryPath : '/Logos/logo-all-white.png',
    allYellow: isRedexTenant ? tenant.branding.logoPrimaryPath : '/Logos/logo-all-yellow.png',
    // Legacy logos (for backward compatibility)
    black: '/Logos/logo-black.png',
    white: '/Logos/logo-white.png',
    whiteOriginal: '/Logos/logo-white-original.png',
  },
  icon: {
    hiveWhite: '/Logos/hive-white.png',
    hiveYellow: '/Logos/hive-yellow.png',
    hiveDark: '/Logos/hive-dark.png',
    favicon: isRedexTenant ? tenant.branding.logoPrimaryPath : faviconPath,
  },
  property: {
    hiveFund: '/Logos/property-card-hive-fund.png',
    hiveFundBanner: '/Logos/property-card-hive-fund-banner.png',
  },
} as const;

export const TENANT = tenant;

// App constants
export const APP_CONSTANTS = {
  // Pagination
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 100,

  // File uploads
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],

  // Staking
  MIN_STAKING_PERIOD_DAYS: 30,
  COMPOUND_FREQUENCY_QUARTERLY: 4,

  // Investment limits
  MIN_INVESTMENT_USD: 100,
  MAX_INVESTMENT_USD: 50000, // Per transaction for compliance

  // UI
  ANIMATION_DURATION: 200,
  TOAST_DURATION: 5000,

  // Security
  JWT_REFRESH_THRESHOLD: 5 * 60 * 1000, // 5 minutes before expiry
  MAX_LOGIN_ATTEMPTS: 5,
  RATE_LIMIT_WINDOW: 15 * 60 * 1000, // 15 minutes

  // WebSocket
  WS_RECONNECT_INTERVAL: 5000,
  WS_MAX_RECONNECT_ATTEMPTS: 10,
} as const;

// Theme configuration (Honeyshares brand)
export const THEME = {
  colors: {
    primary: '#3b82f6', // Blue
    secondary: '#f59e0b', // Honey Yellow
    accent: '#FFD700', // Gold
    danger: '#ef4444', // Red
    success: '#10b981', // Emerald
    warning: '#f59e0b', // Amber
    info: '#06b6d4', // Cyan
  },
  fonts: {
    sans: 'Inter, system-ui, sans-serif',
    mono: 'JetBrains Mono, monospace',
  },
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
} as const;

// Risk disclaimers and legal text
export const LEGAL_DISCLAIMERS = {
  investment: {
    title: 'Investment Risk Disclaimer',
    content: `
      IMPORTANT: Real estate investments involve substantial risk and are not suitable for all investors.
      
      Key Risks:
      • Property values may decline, resulting in loss of principal
      • Dividends are not guaranteed and may fluctuate or cease entirely
      • Liquidity is limited - you may not be able to sell shares when desired
      • Blockchain transactions are irreversible and subject to network fees
      • Regulatory changes may affect the value or legality of your investment
      
      You should only invest money you can afford to lose completely.
    `,
  },
  blockchain: {
    title: 'Blockchain Technology Disclaimer',
    content: `
      This platform uses blockchain technology which involves inherent risks:
      
      • Smart contract vulnerabilities may lead to loss of funds
      • Network congestion may delay transactions or increase fees
      • Private key loss results in permanent loss of access to funds
      • Blockchain transactions are immutable and cannot be reversed
      • You are responsible for maintaining the security of your wallet
      
      Always verify transaction details before confirming.
    `,
  },
  kyc: {
    title: 'Know Your Customer (KYC) Requirements',
    content: `
      Due to regulatory requirements, we must verify your identity before you can:
      
      • Make investments over $1,000
      • Access investor-only features
      • Receive dividend payments
      • Participate in private property offerings
      
      Your personal information is encrypted and stored securely.
      We partner with Persona for identity verification services.
    `,
  },
} as const;
